import PropTypes from 'prop-types';
import { useMemo, useEffect, useState, memo, useRef } from 'react';
import { randomNumber } from '@icp/utils';
import { RemoteCommonInstance } from '@icp/form-renderer-core';
import { useExposedApis } from '../../hooks';
import { useFormApi, useIsInDesign } from '../../FormRenderCtx';
import useActAsField, { EmptyApi } from './useActAsField';

function RemoteCommon({
  jsUrl,
  mountParams,
  shadowHostRef,
  manualLoad,
  actAsFieldApi = EmptyApi,
  setValidatorDelegate,
}) {
  const [error, setError] = useState(null);
  const isInDesign = useIsInDesign();
  const formApi = useFormApi();

  const idToRegister = useMemo(() => `random-extension-id-${randomNumber(100000)}`, []);
  const rcInstance = useMemo(() => new RemoteCommonInstance(), []);

  const exposedApis = useExposedApis();
  if (isInDesign) {
    exposedApis.restApi = rcInstance.mockRestApi;
  }

  const mountParamsRef = useRef(mountParams);
  mountParamsRef.current = mountParams;

  useEffect(() => {
    formApi.asyncComponentManager.register(idToRegister);

    const fieldValueChangeListener = (key, value) => {
      if (key === actAsFieldApi.id) {
        rcInstance.updateValue(value);
      }
    };

    setError(null);
    rcInstance
      .load({ jsUrl, manualLoad })
      .then(() =>
        rcInstance.mount(shadowHostRef.current.shadowRoot || shadowHostRef.current, {
          params: mountParamsRef.current,
          ...exposedApis,
          fieldApi: actAsFieldApi,
        }),
      )
      .then(() => {
        if (rcInstance.validator && setValidatorDelegate) {
          setValidatorDelegate((base) => ({ ...base, test: rcInstance.validator }));
        }
        formApi.on('fieldValueChange', fieldValueChangeListener);
      })
      .catch((err) => {
        console.error(err);
        setError(err);
      })
      .finally(() => {
        formApi.asyncComponentManager.setReady(idToRegister);
      });

    return () => {
      formApi.off('fieldValueChange', fieldValueChangeListener);
      rcInstance.unmount();
    };
  }, [
    rcInstance,
    jsUrl,
    manualLoad,
    shadowHostRef,
    exposedApis,
    formApi,
    actAsFieldApi,
    idToRegister,
    setValidatorDelegate,
  ]);

  useEffect(() => {
    rcInstance.updateParams(mountParams);
  }, [rcInstance, mountParams]);

  return error ? error.message : null;
}

RemoteCommon.propTypes = {
  jsUrl: PropTypes.string,
  mountParams: PropTypes.shape({}),
  manualLoad: PropTypes.bool,
  shadowHostRef: PropTypes.shape({
    current: PropTypes.shape({
      shadowRoot: PropTypes.shape({}),
    }),
  }),
  actAsFieldApi: PropTypes.shape({}),
  setValidatorDelegate: PropTypes.func,
};

function WithFieldApi(props) {
  const { actAsField } = props;
  const [actAsFieldApi, setValidatorDelegate] = useActAsField(actAsField);
  return (
    <RemoteCommon
      {...props}
      actAsFieldApi={actAsFieldApi}
      setValidatorDelegate={setValidatorDelegate}
    />
  );
}

WithFieldApi.propTypes = {
  actAsField: PropTypes.shape({}),
};

function Wrapper(props) {
  const { actAsField } = props;
  if (actAsField) {
    return <WithFieldApi {...props} />;
  }
  return <RemoteCommon {...props} />;
}
Wrapper.propTypes = {
  actAsField: PropTypes.shape({}),
};

export default memo(Wrapper);
